import { Spinner, Card, CardBody } from '@heroui/react';
import React, { useState, useEffect } from 'react';

import { BaseModal } from '../../../../components/BaseModal';
import { ChocoCheckbox } from '../../../../components/FormComponents';
import { useBuyerUsers, useBuyerAdminOperations } from '../../hooks';

interface EnableRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (adminUserIds: string[]) => void;
  buyerId: string;
  buyerName: string;
  buyerReferenceId: string;
  isLoading?: boolean;
}

export const EnableRolesModal: React.FC<EnableRolesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  buyerId,
  buyerName,
  buyerReferenceId,
  isLoading = false,
}) => {
  const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([]);

  const {
    users,
    isLoading: isLoadingUsers,
    error,
  } = useBuyerUsers({
    buyerId,
    isEnabled: isOpen,
  });

  const { addAdmins, isLoading: isSubmitting } = useBuyerAdminOperations({
    onSuccess: (adminUserIds) => {
      onConfirm(adminUserIds);
    },
  });

  // Reset selected admins when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAdminIds([]);
    }
  }, [isOpen]);

  const handleUserToggle = (userId: string) => {
    setSelectedAdminIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleConfirm = async () => {
    if (selectedAdminIds.length === 0) return;

    await addAdmins(buyerId, selectedAdminIds);
  };

  const isConfirmDisabled = selectedAdminIds.length === 0;
  const loading = isLoadingUsers || isLoading;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title={`Choose Admin(s) for ${buyerName}`}
      subtitle={`${buyerReferenceId} - Select at least one team member to be an Admin`}
      footerContent={{
        onCancel: onClose,
        onConfirm: handleConfirm,
        isLoading: isSubmitting,
        isDisabled: isConfirmDisabled,
        cancelText: 'CANCEL',
        confirmText: 'SAVE',
      }}
    >
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" color="primary" />
          <span className="ml-3 text-default-600">Loading team members...</span>
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-8 text-danger">
          <p>
            Error loading team members:{' '}
            {error instanceof Error ? error.message : 'Unknown error occurred'}
          </p>
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="text-center py-8 text-default-500">
          <p>
            No team members found. Add team members first before enabling roles.
          </p>
        </div>
      )}

      {!loading && !error && users.length > 0 && (
        <div className="space-y-4">
          <p className="text-default-600 text-sm">
            Admins can manage other team members and their permissions.
          </p>

          <Card>
            <CardBody className="p-4 max-h-64 overflow-y-auto">
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-2 hover:bg-default-50 rounded-lg transition-colors"
                  >
                    <ChocoCheckbox
                      isSelected={selectedAdminIds.includes(user.id)}
                      onValueChange={() => handleUserToggle(user.id)}
                      size="sm"
                      label={
                        <div>
                          <p className="font-medium text-foreground">
                            {user.name || 'Unknown User'}
                          </p>
                          {user.phone && (
                            <p className="text-sm text-default-500">
                              {user.phone}
                            </p>
                          )}
                        </div>
                      }
                    />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {selectedAdminIds.length > 0 && (
            <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
              <p className="text-sm text-success-800">
                <strong>{selectedAdminIds.length}</strong> admin(s) selected
              </p>
            </div>
          )}
        </div>
      )}
    </BaseModal>
  );
};
